import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import Modal from "../../../../../components/Modal";
import SeatMapPreview from "../../../../../components/seatmap/SeatMapPreview";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import SectionHeading from "../../../../../components/SectionHeading";
import {
    useBusinessSeatMaps,
    useEventSeatMap,
    useAssignSeatMap,
    useDetachSeatMap,
} from "../../../../../hooks/useBusinessSeatMaps";
import {BusinessSeatMapItem} from "../../../../../types/seatmap";

type OutletCtx = { eventId: string | null; isEdit?: boolean };

function Chips({categories}: {categories: string[]}) {
    if (!categories?.length) return null;
    return (
        <div className="mt-2 flex flex-wrap gap-1">
            {categories.map((c) => (
                <span
                    key={c}
                    className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-100"
                >
                    {c}
                </span>
            ))}
        </div>
    );
}

export function SeatMapStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [assignedId, setAssignedId] = useState<string | null>(null);
    const [loadingEvent, setLoadingEvent] = useState(false);
    const [previewId, setPreviewId] = useState<string | null>(null);

    // Load the currently-assigned seat map id from the event.
    useEffect(() => {
        if (!businessId || !eventId) return;
        setLoadingEvent(true);
        (async () => {
            try {
                const {data: ev} = await api.get(`/api/v1/business/${businessId}/events/${eventId}`);
                setAssignedId(ev?.seatmapId ?? ev?.seatMapId ?? ev?.seat_map_id ?? null);
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load event"));
            } finally {
                setLoadingEvent(false);
            }
        })();
    }, [businessId, eventId]);

    const {data: available, isLoading: loadingList} = useBusinessSeatMaps(businessId);

    const assign = useAssignSeatMap(businessId, eventId);
    const detach = useDetachSeatMap(businessId, eventId);
    const busy = assign.isPending || detach.isPending;

    const assignedItem = useMemo(
        () => (available ?? []).find((m) => m.id === assignedId) ?? null,
        [available, assignedId]
    );

    const handleAssign = async (item: BusinessSeatMapItem) => {
        if (item.id === assignedId) return;
        try {
            await assign.mutateAsync(item.id);
            setAssignedId(item.id);
            notify.success(`Assigned "${item.name}"`);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to assign seat map"));
        }
    };

    const handleDetach = async () => {
        try {
            await detach.mutateAsync();
            setAssignedId(null);
            notify.success("Seat map removed");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to remove seat map"));
        }
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/tickets`);
        else navigate(`/business/events/new/tickets?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    const maps = available ?? [];

    return (
        <div className="space-y-6">
            <SectionHeading
                title="Seat Map"
                description="Assign a seat map so attendees can pick their seats. Ticket categories can then be mapped to seat categories on the Tickets step."
            />

            {/* Currently assigned */}
            {assignedId && (
                <div className="rounded-2xl border border-orange-200 bg-orange-50/50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-xs font-medium uppercase tracking-wide text-orange-700">
                                Currently assigned
                            </p>
                            <p className="mt-0.5 font-semibold text-gray-900">
                                {assignedItem?.name ?? "Seat map"}
                            </p>
                            {assignedItem?.description && (
                                <p className="mt-0.5 text-sm text-gray-600">{assignedItem.description}</p>
                            )}
                            {assignedItem?.categories?.length ? (
                                <Chips categories={assignedItem.categories}/>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setPreviewId(assignedId)}
                            >
                                Preview
                            </Button>
                            <Button
                                type="button"
                                variant="tertiary"
                                size="sm"
                                disabled={busy}
                                onClick={handleDetach}
                            >
                                Remove
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Available library */}
            <div>
                <h3 className="text-sm font-semibold text-gray-900">Available seat maps</h3>
                {loadingEvent || loadingList ? (
                    <div className="py-10 text-sm text-gray-500">Loading seat maps…</div>
                ) : maps.length === 0 ? (
                    <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
                        No seat maps are available yet. An administrator can add them to the library.
                    </div>
                ) : (
                    <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {maps.map((item) => {
                            const isAssigned = item.id === assignedId;
                            return (
                                <div
                                    key={item.id}
                                    className={`rounded-2xl border p-4 shadow-sm ${
                                        isAssigned ? "border-orange-400 ring-1 ring-orange-200" : "border-gray-200 bg-white"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="font-semibold text-gray-900">{item.name}</p>
                                        <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
                                            {item.eventCount} events
                                        </span>
                                    </div>
                                    {item.description && (
                                        <p className="mt-1 text-sm text-gray-600">{item.description}</p>
                                    )}
                                    <Chips categories={item.categories}/>
                                    <div className="mt-4 flex items-center gap-2">
                                        <Button
                                            type="button"
                                            variant="tertiary"
                                            size="sm"
                                            onClick={() => setPreviewId(item.id)}
                                        >
                                            Preview
                                        </Button>
                                        <Button
                                            type="button"
                                            variant={isAssigned ? "secondary" : "primary"}
                                            size="sm"
                                            disabled={busy || isAssigned}
                                            onClick={() => handleAssign(item)}
                                        >
                                            {isAssigned ? "Assigned" : "Assign"}
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3">
                <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={() => navigate("/business/events")}
                >
                    Exit
                </Button>
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    disabled={busy}
                    onClick={goNext}
                >
                    Continue to Tickets
                </Button>
            </div>

            {/* Preview modal */}
            {previewId && (
                <SeatMapPreviewModal
                    businessId={businessId}
                    eventId={eventId}
                    seatMapId={previewId}
                    title={maps.find((m) => m.id === previewId)?.name ?? "Seat map"}
                    onClose={() => setPreviewId(null)}
                />
            )}
        </div>
    );
}

/** Fetches a library seat map's layout by id (via the event-scoped endpoint) and renders a read-only preview. */
function SeatMapPreviewModal({
    businessId,
    eventId,
    seatMapId,
    title,
    onClose,
}: {
    businessId: string | null;
    eventId: string | null;
    seatMapId: string;
    title: string;
    onClose: () => void;
}) {
    const {data, isLoading, isError} = useEventSeatMap(businessId, eventId, seatMapId);

    return (
        <Modal
            open
            onClose={onClose}
            title={`Preview — ${data?.name ?? title}`}
            size="full"
            footer={
                <div className="flex justify-end">
                    <Button type="button" variant="secondary" size="md" onClick={onClose}>
                        Close
                    </Button>
                </div>
            }
        >
            {isLoading ? (
                <div className="py-16 text-center text-sm text-gray-500">Loading layout…</div>
            ) : isError || !data?.json ? (
                <div className="py-16 text-center text-sm text-red-500">Unable to load seat map layout.</div>
            ) : (
                <SeatMapPreview seatMap={data}/>
            )}
        </Modal>
    );
}
